import React, { useState } from 'react'
import { Form, Input } from 'antd'
import { Box, Button, IconButton, Paper, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import type { BlockPayload, ISalepageFormProduct } from '../../../types'
import { IProduct } from '../../../../../types/product'
import ImgTag from '../../../../../components/ImgTag'
import { ProductSelectModal } from '../ProductSelectModal'

interface Props {
  payload: { products?: ISalepageFormProduct[]; btnText?: string }
  onChange: (payload: BlockPayload) => void
}

export const FormBlock: React.FC<Props> = ({ payload: p, onChange }) => {
  const products = p?.products ?? []
  const [productModalOpen, setProductModalOpen] = useState(false)

  const toFormProduct = (prod: IProduct): ISalepageFormProduct => ({
    objectId: prod.objectId,
    code: prod.code,
    name: prod.name,
    image: prod.image,
    price: prod.price,
  })

  const handleAddProduct = (product: IProduct) => {
    const formProduct = toFormProduct(product)
    if (products.some((pr) => pr.objectId === formProduct.objectId)) return
    onChange({
      products: [...products, formProduct],
      btnText: p?.btnText ?? 'สั่งซื้อเลย',
    })
  }

  const handleRemoveProduct = (index: number) => {
    const next = products.filter((_, i) => i !== index)
    onChange({ products: next, btnText: p?.btnText ?? 'สั่งซื้อเลย' })
  }

  const handleMoveProduct = (index: number, direction: 'up' | 'down') => {
    const target = direction === 'up' ? index - 1 : index + 1
    if (target < 0 || target >= products.length) return
    const next = [...products]
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange({ products: next, btnText: p?.btnText ?? 'สั่งซื้อเลย' })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          textAlign: 'center',
          borderStyle: 'dashed',
          bgcolor: 'rgba(167, 139, 250, 0.06)',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          ฟอร์มคำสั่งซื้อ — เลือกสินค้าจากระบบเพื่อนำไปขายใน Salepage
        </Typography>
      </Paper>

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => setProductModalOpen(true)}
        sx={{ alignSelf: 'flex-start' }}
      >
        เลือกสินค้า
      </Button>

      <ProductSelectModal
        open={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        selectedProducts={products}
        onSelect={handleAddProduct}
      />

      {products.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            รายการสินค้าที่เลือก ({products.length})
          </Typography>
          {products.map((prod, idx) => (
            <Paper
              key={prod.objectId}
              variant="outlined"
              sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <span style={{ width: 24, textAlign: 'center', fontSize: 12, color: '#8b949e' }}>
                {idx + 1}
              </span>
              <IconButton
                size="small"
                disabled={idx === 0}
                onClick={() => handleMoveProduct(idx, 'up')}
                title="เลื่อนขึ้น"
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={idx === products.length - 1}
                onClick={() => handleMoveProduct(idx, 'down')}
                title="เลื่อนลง"
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
              <ImgTag imageUrl={prod.image} name={prod.name} size={40} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  {prod.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {prod.code} · ฿{prod.price?.toLocaleString()}
                </Typography>
              </Box>
              <IconButton
                size="small"
                color="error"
                onClick={() => handleRemoveProduct(idx)}
                title="ลบ"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Paper>
          ))}
        </Box>
      )}

      <Form.Item label="ข้อความปุ่มสั่งซื้อ" style={{ marginBottom: 0 }}>
        <Input
          placeholder="สั่งซื้อเลย"
          value={p?.btnText ?? ''}
          onChange={(e) =>
            onChange({ products, btnText: e.target.value })
          }
        />
      </Form.Item>
    </Box>
  )
}
